import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

const getToken = () => {
  return localStorage.getItem('token');
};

const bedrockApiClient = axios.create({
  baseURL: config.api.bedrockServiceUrl,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 600000 // 10 minutes
});

bedrockApiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// On 401, attempt a token refresh and retry once before logging out
applyAuthRetryInterceptor(bedrockApiClient);

// Proactively refresh token before any request if within 30-minute expiry window
applyTokenRefreshInterceptor(bedrockApiClient);

const bedrockService = {
  // List documents with extracted data points (Document Data page).
  // Served by the bedrock gateway: the main API gateway is at its route quota.
  // filters (all optional): { clientId, docType, includeUnextracted, includeSummary }
  //   -> { extractions: [...], askEnabled }. clientId: the Client Helper
  //   client the eMoney data is keyed by.
  getExtractions: async ({ clientId, docType, includeUnextracted, includeSummary } = {}) => {
    const params = {};
    if (clientId) params.clientId = clientId;
    if (docType) params.docType = docType;
    if (includeUnextracted) params.includeUnextracted = true;
    if (includeSummary) params.includeSummary = true;
    const response = await bedrockApiClient.get('bedrock/extractions', { params });
    return response.data;
  },

  // Clients with documents -> { clients: [{ clientId, clientName, documentCount }],
  // unassignedCount } (analyzed documents with no client yet).
  // clientName is for display only.
  getExtractionClients: async () => {
    const response = await bedrockApiClient.get('bedrock/extractions', { params: { view: 'clients' } });
    return response.data;
  },

  // Ask a question about one client's extracted data (structured KB, async).
  // -> { job_id, status: 'pending' }; poll getDocumentDataAnswer.
  askDocumentData: async (clientId, question) => {
    const response = await bedrockApiClient.post('bedrock/extractions/ask', {
      client_id: clientId,
      question,
    });
    return response.data;
  },

  // -> { job_id, status, answer, generated_sql, rows, scope_verified, error }
  getDocumentDataAnswer: async (jobId) => {
    const response = await bedrockApiClient.get(`bedrock/extractions/ask/${encodeURIComponent(jobId)}`);
    return response.data;
  },

  // Re-run only the extraction step for one document (one model call).
  // -> { job_id, status: 'pending' }; poll getExtractionRerun.
  rerunExtraction: async (fileId) => {
    const response = await bedrockApiClient.post(`bedrock/extractions/${encodeURIComponent(fileId)}/rerun`);
    return response.data;
  },

  // Re-run extraction on every out-of-date document of a client (server-side,
  // a few at a time). -> { batch_id | null, total, expired }
  bulkRerunExtraction: async (clientId) => {
    const response = await bedrockApiClient.post('bedrock/extractions/bulk-rerun', { clientId });
    return response.data;
  },

  // -> { batch_id, status, total, completed, failed, outcomes, error }
  getBulkRerun: async (batchId) => {
    const response = await bedrockApiClient.get(`bedrock/extractions/bulk-rerun/${encodeURIComponent(batchId)}`);
    return response.data;
  },

  // -> { job_id, file_id, status, extraction_status, prompt_name, error }
  getExtractionRerun: async (jobId) => {
    const response = await bedrockApiClient.get(`bedrock/extractions/rerun/${encodeURIComponent(jobId)}`);
    return response.data;
  },

  // eMoney report edits. Each returns { fileId, annotations } -- the
  // document's corrections, entered-in-eMoney marks and notes after the
  // change. A 409 means someone else changed it first, or the document was
  // re-run since extractionRevision (the report row's) was loaded.

  // path: the field's path in the extracted data, e.g. ['accounts', 1, 'Cash Balance'].
  saveCorrection: async (fileId, { path, value, action = 'set', expectedVersion = 0, extractionRevision }) => {
    const response = await bedrockApiClient.put(
      `bedrock/extractions/${encodeURIComponent(fileId)}/corrections`,
      { path, value, action, expectedVersion, extractionRevision },
    );
    return response.data;
  },

  // expectedValue: the value the user saw; the check is refused if it changed.
  saveSyncMark: async (fileId, { path, synced, expectedValue, extractionRevision }) => {
    const response = await bedrockApiClient.put(
      `bedrock/extractions/${encodeURIComponent(fileId)}/sync`,
      { path, synced, expectedValue, extractionRevision },
    );
    return response.data;
  },

  // Many entered-in-eMoney marks of one document in one save (an account, a
  // section's share, a nested table or row). marks: [{ path, expectedValue }].
  // All or nothing: a changed value is a 409 and nothing is saved.
  saveSyncMarks: async (fileId, { marks, synced, extractionRevision }) => {
    const response = await bedrockApiClient.put(
      `bedrock/extractions/${encodeURIComponent(fileId)}/sync/bulk`,
      { marks, synced, extractionRevision },
    );
    return response.data;
  },

  // accountIndex: the account's position in the document's accounts list
  // (null for a single-account document); empty text clears the note.
  saveNote: async (fileId, { accountIndex, text, expectedVersion = 0, extractionRevision }) => {
    const response = await bedrockApiClient.put(
      `bedrock/extractions/${encodeURIComponent(fileId)}/notes`,
      { accountIndex, text, expectedVersion, extractionRevision },
    );
    return response.data;
  },

  // Every document's annotations for one client, polled by the open report to
  // show other people's changes. -> { clientId, annotations: { [fileId]: {...} }, revisions }
  getClientAnnotations: async (clientId) => {
    const response = await bedrockApiClient.get('bedrock/extractions/annotations', { params: { clientId } });
    return response.data;
  },

  // The report as a PDF or Word file. format: 'pdf' | 'docx'; report: the
  // export model (utils/reportExport.js). -> Blob
  exportReport: async (format, report) => {
    const response = await bedrockApiClient.post('bedrock/extractions/export', { format, report }, { responseType: 'blob' });
    return response.data;
  },

  // -> { fileId, history: [{ kind, action, key, field, old, new, by, at }] }, newest first
  getDocumentHistory: async (fileId) => {
    const response = await bedrockApiClient.get(`bedrock/extractions/${encodeURIComponent(fileId)}/history`);
    return response.data;
  },

  // Get global doc_types list (returns full definitions with id and embedded sub_types)
  getDocTypes: async () => {
    try {
      const response = await bedrockApiClient.get('bedrock/doc-types');
      return response.data; // expected { success, doc_types: [{ id, name, short_description, sub_types: [...], ... }] }
    } catch (error) {
      console.error('Error fetching doc_types:', error);
      throw error;
    }
  },

  // Add a new global doc_type with optional description and sub_types
  addDocType: async (name, short_description = '', sub_types = []) => {
    try {
      const response = await bedrockApiClient.post('bedrock/doc-types', { 
        name, 
        short_description, 
        sub_types 
      });
      return response.data; // expected { success, doc_type: { id, name, ... } }
    } catch (error) {
      console.error('Error adding doc_type:', error);
      throw error;
    }
  },

  // Update a doc_type by ID (can update name, short_description, and sub_types)
  updateDocType: async (id, updates) => {
    try {
      const response = await bedrockApiClient.put(`bedrock/doc-types/${encodeURIComponent(id)}`, updates);
      return response.data; // expected { success, doc_type }
    } catch (error) {
      console.error('Error updating doc_type:', error);
      throw error;
    }
  },

  // Delete a global doc_type by ID
  deleteDocType: async (id) => {
    try {
      const response = await bedrockApiClient.delete(`bedrock/doc-types/${encodeURIComponent(id)}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting doc_type:', error);
      throw error;
    }
  },

  // Get analysis jobs by category
  getAnalysisJobsByCategory: async (category, limit = 100) => {
    try {
      const response = await bedrockApiClient.get(
        `bedrock/analyze/document/s3/by-category/${encodeURIComponent(category)}`,
        { params: { limit } }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching analysis jobs:', error);
      throw error;
    }
  },

  // Update job friendly name
  updateJobFriendlyName: async (jobId, friendlyName) => {
    try {
      const response = await bedrockApiClient.put(
        `bedrock/analyze/document/s3/${jobId}/friendly-name`,
        null,
        { params: { friendly_name: friendlyName } }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating job friendly name:', error);
      throw error;
    }
  },

  // Get job status
  getJobStatus: async (jobId) => {
    try {
      const response = await bedrockApiClient.get(`bedrock/analyze/document/s3/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching job status:', error);
      throw error;
    }
  },

  // Delete a job
  deleteJob: async (jobId) => {
    try {
      const response = await bedrockApiClient.delete(`bedrock/analyze/document/s3/${jobId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting job:', error);
      throw error;
    }
  },

  // ---------------------------------------------------------------------------
  // Temporary Document Chat
  // ---------------------------------------------------------------------------

  /**
   * Ask a question about the uploaded documents.
   * @param {string} sessionId - Session ID from upload
   * @param {string} question - Current user question
   * @param {Array<{role:string, content:string}>} conversationHistory - Prior turns
   * @returns {{ job_id, status }} — 202 Accepted
   */
  submitDocChatQuery: async (sessionId, question, conversationHistory = [], extraParams = {}) => {
    try {
      const response = await bedrockApiClient.post('bedrock/doc-chat/query', {
        session_id: sessionId,
        question,
        conversation_history: conversationHistory,
        max_tokens: 2000,
        ...extraParams,
      });
      return response.data;
    } catch (error) {
      console.error('Error submitting doc chat query:', error);
      throw error;
    }
  },

  /**
   * Poll the status of an async doc-chat query job.
   * @param {string} jobId - Job ID returned by submitDocChatQuery
   * @returns {{ job_id, session_id, status, answer, sources, error }}
   */
  getDocChatJobStatus: async (jobId) => {
    try {
      const response = await bedrockApiClient.get(`bedrock/doc-chat/query/${encodeURIComponent(jobId)}`);
      return response.data;
    } catch (error) {
      console.error('Error polling doc chat job status:', error);
      throw error;
    }
  },

  /**
   * Delete all documents for a session from the temporary S3 prefix.
   * @param {string} sessionId - Session ID to clean up
   */
  deleteTempDocSession: async (sessionId) => {
    try {
      await bedrockApiClient.delete(`bedrock/doc-chat/${encodeURIComponent(sessionId)}`);
    } catch (error) {
      console.error('Error deleting temp doc session:', error);
      throw error;
    }
  },

  /**
   * Submit a doc-chat response for export to PDF or DOCX.
   * Returns { job_id, status: "pending" } — poll getDocChatExportStatus for completion.
   * @param {string} answer - Markdown answer text
   * @param {string[]} sources - Source filenames
   * @param {'pdf'|'docx'} format - Output format
   * @param {number} index - Message index for filename
   */
  submitDocChatExport: async (answer, sources, format, index) => {
    try {
      const response = await bedrockApiClient.post('bedrock/doc-chat/export', {
        answer,
        sources,
        format,
        index,
      });
      return response.data;
    } catch (error) {
      console.error('Error submitting doc-chat export:', error);
      throw error;
    }
  },

  /**
   * Poll the status of an async doc-chat export job.
   * @param {string} jobId - Job ID returned by submitDocChatExport
   * @returns {{ job_id, status, download_url, filename, error }}
   */
  getDocChatExportStatus: async (jobId) => {
    try {
      const response = await bedrockApiClient.get(`bedrock/doc-chat/export/${encodeURIComponent(jobId)}`);
      return response.data;
    } catch (error) {
      console.error('Error polling doc-chat export status:', error);
      throw error;
    }
  },
};

export default bedrockService;